import { useCallback, useEffect, useState } from 'react';
import { api, fmt, fmtDate, num, type User } from '../api.ts';
import DataTable, { type Column } from '../components/DataTable.tsx';
import { useList } from '../lib/useList.ts';

type Tab = 'outstanding' | 'log';

interface OweRow {
  customer_id: number;
  customer_name: string;
  phone: string | null;
  dispatched_value: string;
  paid_value: string;
  outstanding: string;
  oldest_dispatch_date: string | null;
  last_payment_date: string | null;
}

interface PayRow {
  payment_id: number;
  customer_name: string;
  amount: string;
  payment_date: string;
  method: string;
  reference: string | null;
  status: 'ACTIVE' | 'VOIDED';
  notes: string | null;
  entered_by: string | null;
}

interface Statement {
  summary: OweRow;
  dispatches: { dispatch_id: number; job_number: string; po_number: string | null; box_name: string;
                quantity: string; dispatch_date: string; status: string; line_value: string }[];
  payments: { payment_id: number; amount: string; payment_date: string; method: string;
              reference: string | null; status: string }[];
}

const rupees = (v: string | number | null | undefined) => `₹${fmt(v)}`;
const today = () => new Date().toISOString().slice(0, 10);
// An explicit map, not title-casing: UPI is an initialism and any algorithmic
// capitalisation renders it "Upi".
const METHOD_LABEL: Record<string, string> = {
  CASH: 'Cash', BANK: 'Bank transfer', UPI: 'UPI', CHEQUE: 'Cheque', OTHER: 'Other',
};
const METHODS = Object.keys(METHOD_LABEL);

interface OrderOption {
  po_id: number; po_number: string | null; so_number: number;
  po_date: string; status: string; order_value: string; paid: string;
}

export default function Payments({ user, view }: { user: User; view: Tab }) {
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const canManage = user.role === 'ADMIN' || user.role === 'MANAGER';

  useEffect(() => { setError(''); setNotice(''); }, [view]);

  return (
    <div className="panel">
      {error && <div className="error" style={{ margin: 16 }}>{error}</div>}
      {notice && <div style={{ margin: 16 }} className="tag ready notice">{notice}</div>}

      {view === 'outstanding'
        ? <OutstandingTab canManage={canManage} setError={setError} setNotice={setNotice} />
        : <LogTab canManage={canManage} setError={setError} setNotice={setNotice} />}
    </div>
  );
}

type Setters = {
  canManage: boolean;
  setError: (s: string) => void;
  setNotice: (s: string) => void;
};

function OutstandingTab({ canManage, setError, setNotice }: Setters) {
  const [rows, setRows] = useState<OweRow[]>([]);
  const [search, setSearch] = useState('');
  const [owingOnly, setOwingOnly] = useState(true);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [statement, setStatement] = useState<Statement | null>(null);
  const [payFor, setPayFor] = useState<OweRow | null>(null);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<string>('BANK');
  const [reference, setReference] = useState('');
  /*
   * Optional. Naming the order means the payment travels with it if the order
   * is ever corrected onto the right customer. An advance or a round sum on
   * account names nothing, which is what it actually is -- so "" is a real
   * answer here, not an unfilled field.
   */
  const [poId, setPoId] = useState('');
  const [orders, setOrders] = useState<{ po_id: number; label: string }[]>([]);
  const [date, setDate] = useState(today());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const qs = new URLSearchParams();
      if (search.trim()) qs.set('search', search.trim());
      if (owingOnly) qs.set('owingOnly', 'true');
      setRows((await api.get<{ customers: OweRow[] }>(
        `/api/payments/outstanding${qs.toString() ? `?${qs}` : ''}`)).customers);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load balances');
    } finally {
      setLoading(false);
    }
  }, [search, owingOnly, setError]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  async function toggle(id: number) {
    if (expanded === id) { setExpanded(null); setStatement(null); return; }
    setExpanded(id);
    setStatement(null);
    try {
      setStatement(await api.get<Statement>(`/api/payments/customers/${id}`));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the statement');
    }
  }

  async function submit() {
    if (!payFor) return;
    setBusy(true); setError('');
    try {
      const res = await api.post<{ outstanding: number }>('/api/payments', {
        customerId: payFor.customer_id,
        amount: Number(amount),
        method,
        paymentDate: date,
        ...(reference.trim() ? { reference: reference.trim() } : {}),
        ...(poId ? { poId: Number(poId) } : {}),
      });
      setNotice(
        `${rupees(amount)} recorded for ${payFor.customer_name} · ` +
          (res.outstanding > 0 ? `${rupees(res.outstanding)} still due`
            : res.outstanding < 0 ? `${rupees(Math.abs(res.outstanding))} in advance`
            : 'settled in full'),
      );
      setPayFor(null); setAmount(''); setReference(''); setPoId('');
      if (expanded === payFor.customer_id) { setExpanded(null); setStatement(null); }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record the payment');
    } finally {
      setBusy(false);
    }
  }

  const totalDue = rows.reduce((s, r) => s + Math.max(num(r.outstanding), 0), 0);

  return (
    <>
      <div className="stagebar">
        <input className="search" placeholder="Search company"
               value={search} onChange={(e) => setSearch(e.target.value)} />
        <label className="hint" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={owingOnly}
                 onChange={(e) => setOwingOnly(e.target.checked)} />
          With a balance only
        </label>
        <span className="hint" style={{ alignSelf: 'center', marginLeft: 'auto' }}>
          Values are pre-GST. Payments are recorded per company, not matched to shipments.
        </span>
      </div>

      {!loading && rows.length === 0 ? (
        <div className="empty">Nothing outstanding.</div>
      ) : (
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>Company</th><th>Phone</th>
                <th className="num">Shipped</th><th className="num">Received</th>
                <th className="num">Due</th>
                <th>Oldest shipment</th><th>Last payment</th><th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const owed = num(r.outstanding);
                return (
                  <tr key={r.customer_id}>
                    <td>{r.customer_name}</td>
                    <td className="dim">{r.phone ?? '—'}</td>
                    <td className="num dim">{rupees(r.dispatched_value)}</td>
                    <td className="num dim">{rupees(r.paid_value)}</td>
                    <td className="num">
                      {owed < 0
                        ? <span className="tag ready">{rupees(Math.abs(owed))} advance</span>
                        : owed === 0
                          ? <span className="dim">—</span>
                          : <strong>{rupees(owed)}</strong>}
                    </td>
                    <td className="dim">{fmtDate(r.oldest_dispatch_date)}</td>
                    <td className="dim">{fmtDate(r.last_payment_date)}</td>
                    <td>
                      <button className="btn" onClick={() => toggle(r.customer_id)}>
                        {expanded === r.customer_id ? 'Hide' : 'Statement'}
                      </button>
                      {canManage && (
                        <button className="btn primary" style={{ marginLeft: 6 }}
                                onClick={() => {
                                  setPayFor(r);
                                  setAmount(owed > 0 ? String(owed) : '');
                                  setMethod('BANK'); setReference(''); setDate(today());
                                  setPoId(''); setOrders([]);
                                  setNotice('');
                                  api.get<{ orders: OrderOption[] }>(
                                    `/api/customers/${r.customer_id}/orders`)
                                    .then((o) => setOrders(o.orders.map((x) => ({
                                      po_id: x.po_id,
                                      label: `SO${x.so_number}`
                                             + (x.po_number ? ` · PO ${x.po_number}` : '')
                                             + ` · ${fmtDate(x.po_date)}`
                                             + ` · ${rupees(x.order_value)}`
                                             + (Number(x.paid) > 0
                                                 ? ` · ${rupees(x.paid)} paid` : ''),
                                    }))))
                                    .catch(() => setOrders([]));
                                }}>
                          Record payment
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {rows.length > 1 && (
                <tr>
                  <td colSpan={4} className="dim"><strong>Total due</strong></td>
                  <td className="num"><strong>{rupees(totalDue)}</strong></td>
                  <td colSpan={3} />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {expanded !== null && (
        <div className="subrow" style={{ padding: 16, borderTop: '1px solid var(--line)' }}>
          {!statement ? <span className="dim">Loading…</span> : (
            <div className="row2">
              <div>
                <strong>Shipments</strong>
                {statement.dispatches.length === 0
                  ? <div className="hint">Nothing shipped yet.</div>
                  : statement.dispatches.map((d) => (
                      <div key={d.dispatch_id} className="hint">
                        {fmtDate(d.dispatch_date)} · {d.job_number} · {d.box_name} ·{' '}
                        {fmt(d.quantity)} · {rupees(d.line_value)}
                      </div>
                    ))}
              </div>
              <div>
                <strong>Payments</strong>
                {statement.payments.length === 0
                  ? <div className="hint">No payments recorded.</div>
                  : statement.payments.map((p) => (
                      <div key={p.payment_id} className="hint"
                           style={p.status === 'VOIDED'
                             ? { textDecoration: 'line-through' } : undefined}>
                        {fmtDate(p.payment_date)} · {rupees(p.amount)} · {p.method}
                        {p.reference ? ` · ${p.reference}` : ''}
                        {p.status === 'VOIDED' ? ' · voided' : ''}
                      </div>
                    ))}
              </div>
            </div>
          )}
        </div>
      )}

      {payFor && (
        <div className="backdrop" onClick={() => setPayFor(null)}>
          <div className="modal" style={{ width: 500 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3>Payment from {payFor.customer_name}</h3>
              <p>
                {rupees(payFor.dispatched_value)} shipped ·{' '}
                {rupees(payFor.paid_value)} received ·{' '}
                {rupees(payFor.outstanding)} due
              </p>
            </div>
            <div className="modal-body">
              <div className="row2">
                <div className="field">
                  <label htmlFor="amt" className="req">Amount</label>
                  <input id="amt" type="number" min="0.01" step="0.01" value={amount} autoFocus
                         onChange={(e) => setAmount(e.target.value)} />
                  <div className="hint">Prefilled with the full balance</div>
                </div>
                <div className="field">
                  <label htmlFor="pdt">Date received</label>
                  <input id="pdt" type="date" value={date}
                         onChange={(e) => setDate(e.target.value)} />
                </div>
                <div className="field">
                  <label htmlFor="mth">Method</label>
                  <select id="mth" value={method} onChange={(e) => setMethod(e.target.value)}>
                    {METHODS.map((m) => (
                      <option key={m} value={m}>{METHOD_LABEL[m]}</option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="ref">Reference</label>
                  <input id="ref" type="text" placeholder="UTR, cheque no."
                         value={reference} onChange={(e) => setReference(e.target.value)} />
                </div>
                <div className="field">
                  <label htmlFor="po">For which order</label>
                  <select id="po" value={poId} onChange={(e) => setPoId(e.target.value)}>
                    <option value="">Not for a specific order</option>
                    {orders.map((o) => (
                      <option key={o.po_id} value={o.po_id}>{o.label}</option>
                    ))}
                  </select>
                  <div className="hint">
                    Leave this alone for an advance, a round sum on account, or one
                    payment covering several orders. Naming an order keeps the money
                    with it if the order is ever corrected onto another company.
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn" onClick={() => setPayFor(null)} disabled={busy}>Cancel</button>
              <button className="btn primary" onClick={submit}
                      disabled={busy || !amount || Number(amount) <= 0}>
                {busy ? 'Saving…' : 'Record payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function LogTab({ canManage, setError, setNotice }: Setters) {
  const list = useList<PayRow>('/api/payments', 'payments',
                               { sort: 'payment_date', dir: 'desc' });
  const load = list.reload;
  const [busy, setBusy] = useState<number | null>(null);

  async function voidIt(r: PayRow) {
    const reason = window.prompt(
      `Voiding keeps the record and removes ${rupees(r.amount)} from the balance.\nWhy?`,
    );
    if (reason === null || !reason.trim()) return;
    setBusy(r.payment_id); setError('');
    try {
      const res = await api.post<{ outstanding: number }>(
        `/api/payments/${r.payment_id}/void`, { reason: reason.trim() });
      setNotice(`Voided · ${r.customer_name} now shows ${rupees(res.outstanding)} due`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not void the payment');
    } finally {
      setBusy(null);
    }
  }

  const columns: Column<PayRow>[] = [
    { key: 'payment_date', label: 'Date', type: 'date',
      render: (r) => fmtDate(r.payment_date) },
    { key: 'customer_name', label: 'Company' },
    { key: 'amount', label: 'Amount', type: 'number', align: 'right',
      render: (r) => rupees(r.amount) },
    { key: 'method', label: 'Method', type: 'choice',
      choices: ['CASH', 'BANK', 'UPI', 'CHEQUE', 'OTHER']
        .map((v) => ({ value: v, label: v.charAt(0) + v.slice(1).toLowerCase() })),
      render: (r) => <span className="dim">{r.method}</span> },
    { key: 'reference', label: 'Reference', secondary: true,
      render: (r) => <span className="dim">{r.reference ?? '—'}</span> },
    { key: 'entered_by', label: 'Recorded by', secondary: true,
      render: (r) => <span className="dim">{r.entered_by ?? '—'}</span> },
    { key: 'status', label: 'Status', type: 'choice',
      choices: [
        { value: 'ACTIVE', label: 'Active' },
        { value: 'VOIDED', label: 'Voided' },
      ],
      render: (r) => (
        <span className={`tag ${r.status === 'VOIDED' ? 'blocked' : 'ready'}`}>
          {r.status === 'VOIDED' ? 'Voided' : 'Active'}
        </span>
      ) },
    {
      key: 'act', label: '', sortable: false, filterable: false,
      render: (r) => (canManage && r.status === 'ACTIVE' ? (
        <button className="btn" disabled={busy === r.payment_id}
                onClick={() => voidIt(r)}>Void</button>
      ) : null),
    },
  ];

  return (
    <DataTable
      list={list}
      columns={columns}
      exportName="/api/payments"
      rowKey={(r) => r.payment_id}
      /* A voided payment stays on the log, struck through: the money was
         recorded once, and hiding it would make the balance unexplainable. */
      rowClass={(r) => (r.status === 'VOIDED' ? 'struck' : '')}
      empty="No payments recorded."
    />
  );
}
